// Typed fetch wrapper — handles JWT, refresh-on-401
// JWT stored in localStorage (acceptable for SPA admin-only portal)

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

const ACCESS_TOKEN_KEY = 'boligflow_access_token'
const REFRESH_TOKEN_KEY = 'boligflow_refresh_token'

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export function setTokens(access: string, refresh: string) {
  localStorage.setItem(ACCESS_TOKEN_KEY, access)
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

let isRefreshing = false
let refreshQueue: Array<(token: string) => void> = []

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return null

  const resp = await fetch(`${API_URL}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  if (!resp.ok) {
    clearTokens()
    return null
  }

  const data = await resp.json()
  const newAccess = data.access_token as string
  setTokens(newAccess, refreshToken)
  return newAccess
}

/**
 * Refresh-aware fetch that returns the raw Response (for blobs/PDFs).
 * Same 401 → refresh → retry behaviour as apiFetch, but the caller reads the
 * body (e.g. .blob()). Used by the PDF preview so it doesn't 401 on an expired
 * access token mid-session.
 */
export async function apiFetchRaw(path: string, options: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = { ...(options.headers as Record<string, string>) }
  const token = getAccessToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  let response = await fetch(`${API_URL}${path}`, { ...options, headers })

  if (response.status === 401) {
    const newToken = await refreshAccessToken()
    if (!newToken) {
      clearTokens()
      window.location.href = '/login'
      throw new Error('Session expired')
    }
    headers['Authorization'] = `Bearer ${newToken}`
    response = await fetch(`${API_URL}${path}`, { ...options, headers })
  }

  return response
}

/**
 * Fetch a PDF from an authed endpoint (Authorization header) and open it in a new tab.
 * The API ignores `?token=` query params (HTTPBearer reads the header only), so
 * `window.open(url + '?token=' + jwt)` 401s. Always route authed PDF opens through this.
 */
export async function openPdfInNewTab(path: string): Promise<void> {
  const resp = await apiFetchRaw(path)
  if (!resp.ok) throw new Error(`Kunne ikke hente PDF (${resp.status})`)
  const blobUrl = URL.createObjectURL(await resp.blob())
  window.open(blobUrl, '_blank')
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getAccessToken()
  // For FormData (file upload) the browser must set Content-Type itself so the
  // multipart boundary is included — never force application/json.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string>),
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_URL}${path}`, { ...options, headers })

  // 401 → try refresh once
  if (response.status === 401 && !isRefreshing) {
    isRefreshing = true
    const newToken = await refreshAccessToken()
    isRefreshing = false

    if (!newToken) {
      // Refresh failed — redirect to login
      clearTokens()
      window.location.href = '/login'
      throw new Error('Session expired')
    }

    // Drain the queue
    refreshQueue.forEach((cb) => cb(newToken))
    refreshQueue = []

    // Retry original request
    headers['Authorization'] = `Bearer ${newToken}`
    const retried = await fetch(`${API_URL}${path}`, { ...options, headers })
    if (!retried.ok) {
      const err = await retried.json().catch(() => ({ error: retried.statusText }))
      throw new ApiError(retried.status, errorMessage(err))
    }
    return retried.json() as Promise<T>
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }))
    throw new ApiError(response.status, errorMessage(err))
  }

  return response.json() as Promise<T>
}

/** FastAPI returns `detail` as a string, or as a list of {loc, msg} for 422s. */
function errorMessage(err: { detail?: unknown; error?: unknown }): string {
  const d = err.detail ?? err.error
  if (Array.isArray(d)) {
    return d.map((x: { msg?: string; loc?: unknown[] }) =>
      [x.loc?.slice(-1)[0], x.msg].filter(Boolean).join(': ')).join('; ') || 'Request failed'
  }
  return typeof d === 'string' && d ? d : 'Request failed'
}

export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}
